import type { ScoredResult, IntakeData } from '../engine/types';
import AxisBar from '../components/result/AxisBar';
import BandLabel from '../components/result/BandLabel';
import CtaCard from '../components/result/CtaCard';
import { ARCHETYPE_COPY, BAND_TONE } from '../schemas/board-ai-literacy.copy';
import { topThreeRecommendations } from '../schemas/board-ai-literacy.recommendations';
import type { ArchetypeId } from '../engine/archetype';

interface Props {
  result: ScoredResult;
  intake?: IntakeData;
}

const BAND_LABEL: Record<string, string> = {
  'at-risk': 'At Risk',
  maturing: 'Maturing',
  mature: 'Mature',
};

const BAND_MESSAGE: Record<string, string> = {
  'at-risk':
    'Your board has visible gaps in both governance scaffolding and director capability. This is the typical starting point, and it is fixable in 90 days with the right structure.',
  maturing:
    'Your board has some of the right scaffolding in place, but the strengths are uneven. The 90-day priority is bringing the weaker axis up to match.',
  mature:
    'Your board is in the top tier for AI literacy and governance maturity among Australian SMBs. You probably do not need a fractional CAIO yet, though a quarterly check-in is worth the discipline.',
};

const TONE_STROKE: Record<string, string> = {
  danger: 'var(--ix-danger)',
  warning: 'var(--ix-warning)',
  success: 'var(--ix-success)',
};

function ScoreGauge({ value, tone }: { value: number; tone: string }) {
  const r = 52;
  const circumference = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const dash = (pct / 100) * circumference;
  return (
    <svg viewBox="0 0 120 120" className="h-32 w-32 shrink-0" role="img" aria-label={`Score ${value} out of 100`}>
      <circle cx="60" cy="60" r={r} fill="none" stroke="var(--ix-bg-page)" strokeWidth="11" />
      <circle
        cx="60"
        cy="60"
        r={r}
        fill="none"
        stroke={TONE_STROKE[tone] ?? TONE_STROKE.warning}
        strokeWidth="11"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        transform="rotate(-90 60 60)"
        style={{ transition: 'stroke-dasharray 0.8s ease' }}
      />
      <text
        x="60"
        y="62"
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-heading font-serif"
        style={{ fontSize: '40px', fontWeight: 700 }}
      >
        {value}
      </text>
    </svg>
  );
}

// Lightweight inline section icons (no extra deps), inherit currentColor.
const iconCls = 'h-5 w-5 shrink-0 text-brand';
const ChartIcon = () => (
  <svg className={iconCls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M3 3v18h18" strokeLinecap="round" />
    <rect x="7" y="11" width="3" height="6" rx="1" fill="currentColor" stroke="none" />
    <rect x="13" y="7" width="3" height="10" rx="1" fill="currentColor" stroke="none" />
  </svg>
);
const FlagIcon = () => (
  <svg className={iconCls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M5 21V4m0 0h11l-2 4 2 4H5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function BoardAiLiteracyResult({ result, intake }: Props) {
  const axes = result.score as Record<string, number>;
  const tone = BAND_TONE[result.band] ?? 'warning';
  const archetypeCopy = result.archetype
    ? ARCHETYPE_COPY[result.archetype as ArchetypeId]
    : null;
  const recs = topThreeRecommendations(result.answers);
  const bandLabel = BAND_LABEL[result.band] ?? result.band;
  const bandMessage = BAND_MESSAGE[result.band] ?? '';

  return (
    <article className="space-y-6">
      {/* Score hero */}
      <header className="rounded-2xl bg-surface-2 p-6 shadow-xl shadow-black/30 sm:p-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
          <ScoreGauge value={result.composite ?? 0} tone={tone} />
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <BandLabel band={{ id: result.band, label: bandLabel }} tone={tone} />
              {intake?.name && (
                <span className="text-sm text-muted">Hello {intake.name.split(' ')[0]}</span>
              )}
            </div>
            {bandMessage && <p className="max-w-prose text-heading">{bandMessage}</p>}
          </div>
        </div>
      </header>

      {/* Axes */}
      <section aria-labelledby="axes-h" className="rounded-2xl bg-surface-2 p-6 shadow-lg shadow-black/20 sm:p-8">
        <h2 id="axes-h" className="mb-5 flex items-center gap-2 font-serif text-xl text-heading">
          <ChartIcon />
          Where you stand on each axis
        </h2>
        <div className="space-y-6">
          <AxisBar label="Governance Maturity" value={axes.governance ?? 0} />
          <AxisBar label="Director Capability" value={axes.capability ?? 0} />
        </div>
      </section>

      {archetypeCopy && (
        <section
          aria-labelledby="arch-h"
          className="overflow-hidden rounded-2xl bg-surface-2 shadow-lg shadow-black/20"
        >
          <div className="border-l-4 border-brand p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-brand">Your archetype</p>
            <h2 id="arch-h" className="mt-2 font-serif text-2xl text-heading">
              {archetypeCopy.title}
            </h2>
            <p className="mt-2 max-w-prose text-heading">{archetypeCopy.body}</p>
          </div>
        </section>
      )}

      {/* Priorities — or a maintain message when there are no real gaps */}
      <section aria-labelledby="rec-h" className="rounded-2xl bg-surface-2 p-6 shadow-lg shadow-black/20 sm:p-8">
        {recs.length > 0 ? (
          <>
            <h2 id="rec-h" className="mb-5 flex items-center gap-2 font-serif text-xl text-heading">
              <FlagIcon />
              Your priorities for the next 90 days
            </h2>
            <ol className="space-y-4">
              {recs.map((r, i) => (
                <li key={r.questionId} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-bg font-serif text-lg font-bold text-white">
                    {i + 1}
                  </span>
                  <div className="pt-1">
                    <div className="font-semibold text-heading">{r.prompt}</div>
                    <p className="mt-1 text-sm text-muted">{r.action}</p>
                  </div>
                </li>
              ))}
            </ol>
          </>
        ) : (
          <>
            <h2 id="rec-h" className="mb-3 flex items-center gap-2 font-serif text-xl text-heading">
              <FlagIcon />
              No urgent gaps to fix
            </h2>
            <p className="max-w-prose text-heading">
              You answered at the top of every question, so there is no 90-day remediation list.
              The work now is holding the line: keep a quarterly board AI review so you stay ahead
              as ISO/IEC 42001, APRA guidance, and the Voluntary AI Safety Standard evolve.
            </p>
          </>
        )}
      </section>

      {result.coverageFlags && result.coverageFlags.length > 0 && (
        <section className="rounded-2xl bg-warning/10 p-5 text-sm text-heading">
          You answered &ldquo;Don&rsquo;t know&rdquo; on a meaningful number of questions in{' '}
          {result.coverageFlags.join(', ')}. That itself is a signal, and this dimension is worth a
          deeper review.
        </section>
      )}

      <section className="space-y-3">
        <CtaCard
          variant="primary"
          ctaId="book-briefing"
          label="Book a 30-minute Board AI Briefing"
          url={`/contact/?source=board-ai-literacy&band=${result.band}${
            result.archetype ? `&archetype=${result.archetype}` : ''
          }`}
          subtitle="No prep needed. Bring the report and we will walk through what to fix first."
        />
        <CtaCard
          ctaId="caio-service-page"
          label="Read about Fractional CAIO services"
          url="/services/fractional-chief-ai-officer/"
        />
        <CtaCard
          ctaId="ai-readiness-external"
          label="For IT readiness, the AI Readiness Assessment"
          url="https://aireadiness.innovatexsolutions.com.au/"
          subtitle="A companion tool for your IT team if you are preparing to deploy AI."
        />
      </section>
    </article>
  );
}
